import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Sparkles, PlayCircle } from "lucide-react";

/** Iter163: Bulk-Duplikat-Merger Dialog.
 *
 *  Öffnet sich beim Klick auf "Alle Duplikate mergen"-Button im Belege-Header.
 *  Führt zuerst einen dry_run aus (Preview), zeigt betroffene Gruppen und
 *  lässt den User bestätigen bevor tatsächlich gemerged wird.
 */
export function BulkMergerDialog({ companyId, open, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [executing, setExecuting] = useState(false);

  useEffect(() => {
    if (!open || !companyId) return;
    setLoading(true);
    setPreview(null);
    apiClient
      .post(`/companies/${companyId}/receipts/bulk-merge-duplicates`, {
        dry_run: true,
      })
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Preview fehlgeschlagen"))
      .finally(() => setLoading(false));
  }, [open, companyId]);

  const doMerge = async () => {
    setExecuting(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/receipts/bulk-merge-duplicates`,
        { dry_run: false }
      );
      toast.success(
        `${r.data.total_merges} Duplikat(e) gemerged in ${r.data.processed} Gruppe(n)${
          r.data.skipped ? ` · ${r.data.skipped} Gruppe(n) übersprungen` : ""
        }`
      );
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-Merge fehlgeschlagen");
    } finally {
      setExecuting(false);
    }
  };

  const totalMerges = preview
    ? preview.groups
        .filter((g) => !g.skipped)
        .reduce((s, g) => s + g.discarded_ids.length, 0)
    : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-y-auto"
        data-testid="bulk-merger-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            Bulk-Duplikat-Merger
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="p-6 text-center text-sm text-slate-500">
            Analysiere Duplikate…
          </div>
        )}

        {!loading && preview && preview.total_groups === 0 && (
          <div className="p-6 text-center text-sm text-slate-500">
            Keine Duplikat-Gruppen gefunden. Alles sauber! 🎉
          </div>
        )}

        {!loading && preview && preview.total_groups > 0 && (
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-1 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-xs text-emerald-700 font-medium">
                  Wird gemerged
                </div>
                <div className="text-2xl font-bold text-emerald-900">
                  {totalMerges}
                </div>
                <div className="text-xs text-emerald-700">
                  in {preview.processed} Gruppe(n)
                </div>
              </div>
              {preview.skipped > 0 && (
                <div className="flex-1 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="text-xs text-amber-700 font-medium">
                    Übersprungen
                  </div>
                  <div className="text-2xl font-bold text-amber-900">
                    {preview.skipped}
                  </div>
                  <div className="text-xs text-amber-700">
                    Gruppe(n) mit &gt;1 verbuchten Beleg
                  </div>
                </div>
              )}
            </div>

            <div className="text-sm text-slate-600">
              Die KI wählt automatisch pro Gruppe den Beleg mit dem höchsten
              Qualitäts-Score als „Keep&quot;. Die anderen werden als
              <code className="mx-1 bg-slate-100 px-1 rounded text-xs">superseded</code>
              markiert und aus der Belege-Liste ausgeblendet.
            </div>

            <div className="border border-slate-200 rounded-lg divide-y">
              {preview.groups.map((g, idx) => (
                <div
                  key={g.duplicate_signature}
                  className="p-3"
                  data-testid={`bulk-merge-group-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-mono text-slate-500">
                      Signatur {g.duplicate_signature.slice(0, 12)}…
                    </div>
                    {g.skipped ? (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 border text-[10px]">
                        Übersprungen
                      </Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-[10px]">
                        {g.discarded_ids.length} × Merge
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    {g.total_items} Belege · Keep-Score {g.keep_score ?? "—"}
                    {g.skip_reason && (
                      <span className="text-amber-700">
                        {" · "}{g.skip_reason}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button
                variant="ghost"
                onClick={onClose}
                data-testid="bulk-merger-cancel"
              >
                Abbrechen
              </Button>
              <Button
                onClick={doMerge}
                disabled={executing || totalMerges === 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="bulk-merger-confirm"
              >
                <PlayCircle className="h-4 w-4 mr-1" />
                {executing
                  ? "Merge läuft…"
                  : `${totalMerges} Duplikat(e) mergen`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default BulkMergerDialog;
